import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { X, Save, AlertTriangle } from 'lucide-react';
import { erpToast } from './ERPToast';
import '../../styles/erp-theme.css';
import { API, apiFetch } from '../../lib/api';

/**
 * Generic edit modal for super admin row mutations.
 *
 * Props:
 *   table          (string)  e.g. "ClientMaster" — used to fetch the field whitelist + drive PUT
 *   row            (object)  Row being edited
 *   pk             (string)  Primary key field name on row (e.g. ClientId / SlNo / LicenseId / PricingId)
 *   readOnly       (bool)    If true, all fields are disabled (View mode)
 *   onClose        (fn)
 *   onSaved        (fn)      Called after a successful update so caller can refresh
 *   title          (string)  Modal title override
 */
export function ERPEditModal({ table, row, pk, readOnly = false, onClose, onSaved, title }) {
  const [allowed, setAllowed] = useState(null);
  const [form, setForm]       = useState({});
  const [saving, setSaving]   = useState(false);
  const [error, setError]     = useState(null);

  useEffect(() => {
    let cancelled = false;
    apiFetch(`${API}/licensor/allowed-fields/${table}`)
      .then(r => r.json())
      .then(d => {
        if (cancelled) return;
        if (d.error) setError(d.error);
        else {
          setAllowed(d.fields);
          // Pre-fill from row
          const init = {};
          d.fields.forEach(f => { init[f] = row?.[f] ?? ''; });
          setForm(init);
        }
      })
      .catch(e => !cancelled && setError(e.message));
    return () => { cancelled = true; };
  }, [table, row]);

  const change = (f, v) => setForm(p => ({ ...p, [f]: v }));

  const save = async () => {
    if (!row || !row[pk]) {
      setError(`Missing primary key (${pk})`);
      return;
    }
    setSaving(true);
    setError(null);
    try {
      const res  = await apiFetch(`${API}/licensor/row/${table}/${encodeURIComponent(row[pk])}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(form)
      });
      const data = await res.json();
      if (!res.ok || data.error) {
        setError(data.error || `HTTP ${res.status}`);
        erpToast.error(`Save failed: ${data.error || res.status}`);
      } else {
        erpToast.success(`${table} updated (${data.fieldsUpdated?.length || 0} fields)`);
        onSaved && onSaved({ ...row, ...form });
        onClose && onClose();
      }
    } catch (e) {
      setError(e.message);
      erpToast.error(`Save failed: ${e.message}`);
    } finally {
      setSaving(false);
    }
  };

  if (typeof document === 'undefined') return null;

  return createPortal(
    <div
      style={{
        position: 'fixed', inset: 0, background: 'rgba(15,23,42,0.55)',
        display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 9000
      }}
      onClick={(e) => e.target === e.currentTarget && onClose && onClose()}
      data-testid="erp-edit-modal-overlay"
    >
      <div
        style={{
          width: 760, maxHeight: '88vh', display: 'flex', flexDirection: 'column',
          background: '#fff', borderRadius: 12, boxShadow: '0 20px 60px rgba(0,0,0,0.35)',
          overflow: 'hidden'
        }}
        data-testid="erp-edit-modal"
      >
        {/* Header */}
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '14px 20px', borderBottom: '1px solid #e2e8f0', background: '#f8fafc' }}>
          <div>
            <div style={{ fontSize: 11, color: '#3b82f6', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: 2 }}>
              {readOnly ? 'VIEW' : 'EDIT'} ROW
            </div>
            <div style={{ fontSize: 15, fontWeight: 900, color: '#0f172a' }}>
              {title || `${table} — ${row?.[pk] ?? ''}`}
            </div>
          </div>
          <button
            onClick={onClose}
            style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#64748b', padding: 4 }}
            data-testid="erp-edit-modal-close"
          >
            <X size={18} />
          </button>
        </div>

        {/* Body */}
        <div style={{ flex: 1, overflowY: 'auto', padding: 20 }}>
          {error && (
            <div style={{ display: 'flex', gap: 8, padding: 10, background: '#fef2f2', border: '1px solid #fecaca', borderRadius: 6, marginBottom: 12, alignItems: 'center' }}>
              <AlertTriangle size={14} color="#dc2626" />
              <div style={{ fontSize: 12, color: '#991b1b' }}>{error}</div>
            </div>
          )}
          {!allowed ? (
            <div style={{ padding: 30, textAlign: 'center', color: '#94a3b8', fontSize: 12 }}>Loading editable fields…</div>
          ) : (
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 12 }}>
              {allowed.map(f => {
                const v = form[f];
                const isBool = typeof row?.[f] === 'boolean' || /^(IsActive|IsFree|IsSalesDefined|HasSupport)$/.test(f);
                const isNum = typeof row?.[f] === 'number' && !isBool;
                return (
                  <div key={f}>
                    <label style={{ fontSize: 9, fontWeight: 700, color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: 4, display: 'block' }}>{f}</label>
                    {isBool ? (
                      <select
                        className="erp-input"
                        value={v === true || v === 1 || v === '1' ? '1' : '0'}
                        onChange={e => change(f, e.target.value === '1')}
                        disabled={readOnly}
                        data-testid={`erp-edit-${f}`}
                      >
                        <option value="1">true</option>
                        <option value="0">false</option>
                      </select>
                    ) : (
                      <input
                        className="erp-input"
                        type={isNum ? 'number' : 'text'}
                        value={v ?? ''}
                        onChange={e => change(f, isNum ? (e.target.value === '' ? null : Number(e.target.value)) : e.target.value)}
                        disabled={readOnly}
                        data-testid={`erp-edit-${f}`}
                      />
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Footer */}
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, padding: '12px 20px', borderTop: '1px solid #e2e8f0', background: '#fafbfc' }}>
          <button className="erp-btn erp-btn-ghost" onClick={onClose} data-testid="erp-edit-cancel">
            {readOnly ? 'Close' : 'Cancel'}
          </button>
          {!readOnly && (
            <button className="erp-btn erp-btn-primary" onClick={save} disabled={saving || !allowed} data-testid="erp-edit-save">
              <Save size={12} /> {saving ? 'Saving…' : 'Save Changes'}
            </button>
          )}
        </div>
      </div>
    </div>,
    document.body
  );
}

export default ERPEditModal;
